import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useElectronNotification } from "@/react/hooks/notifications/useElectronNotification";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useLocalStorage } from "@circle-react/hooks/utils/useLocalStorage";

export const TABS = {
  INBOX: "inbox",
  CONNECT: "connect",
  MENTIONS: "mentions",
  FOLLOWING: "following",
  ALL: "all",
  ARCHIVED: "archived",
} as const;

export type Tab = (typeof TABS)[keyof typeof TABS];

const TAB_LABELS = {
  [TABS.INBOX]: t("notifications.group_tabs.inbox"),
  [TABS.CONNECT]: t("notifications.group_tabs.connect"),
  [TABS.MENTIONS]: t("notifications.group_tabs.mentions"),
  [TABS.FOLLOWING]: t("notifications.group_tabs.following"),
  [TABS.ALL]: t("notifications.group_tabs.all"),
  [TABS.ARCHIVED]: t("notifications.group_tabs.archived"),
} as const;

export interface NotificationContextProps {
  activeTab: Tab;
  getTabLabel: (tab: Tab) => string;
  lastMarkedAsRead: number | null;
  notificationCount: number;
  onClose?: () => void;
  setActiveTab: (tab: Tab) => void;
  setLastMarkedAsRead: (lastMarkedAsRead: number | null) => void;
  setNotificationCount: (count: number) => void;
  setNotificationTabCounters: (notifications: {
    inbox: number;
    mentions: number;
    notifications: number;
  }) => void;
}

const NotificationContext = createContext<NotificationContextProps | null>(
  null,
);

NotificationContext.displayName = "NotificationContext";

const NOTIFICATION_LAST_MARKED_AS_READ_KEY = "notification_last_marked_as_read";

export interface NotificationProviderProps {
  children: ReactNode;
  onClose?: () => void;
}

export const NotificationProvider = ({
  children,
  onClose,
}: NotificationProviderProps) => {
  const getTabLabel = (tab: Tab) => TAB_LABELS[tab];
  const { setDesktopAppNotificationCount } = useElectronNotification();

  const [activeTab, setActiveTab] = useState<Tab>(TABS.INBOX);
  const [notificationTabCounters, setNotificationTabCounters] = useState({
    inbox: 0,
    mentions: 0,
    notifications: 0,
  });
  const { setValue: setLastMarkedAsRead, getValue } = useLocalStorage<
    number | null
  >(NOTIFICATION_LAST_MARKED_AS_READ_KEY);

  const lastMarkedAsRead = getValue() || null;
  const { notifications } = notificationTabCounters;
  const notificationCount = notifications;

  const setNotificationCount = (count: number) => {
    setNotificationTabCounters({
      ...notificationTabCounters,
      notifications: count,
    });
  };

  useEffect(() => {
    setDesktopAppNotificationCount(notificationCount);
  }, [notificationCount]);

  return (
    <NotificationContext.Provider
      value={{
        activeTab,
        setActiveTab,
        notificationCount,
        setNotificationCount,
        getTabLabel,
        onClose,
        setNotificationTabCounters,
        lastMarkedAsRead,
        setLastMarkedAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }

  return context;
};

export const useTabsList = () => {
  const isConnectEnabled = useIsConnectEnabled();

  return isConnectEnabled
    ? Object.values(TABS)
    : Object.values(TABS).filter(tab => tab !== TABS.CONNECT);
};
